// ─── ArtifactDocView (ocn-S241-P0) ───────────────────────────────
// Renders a single scaffolding artifact's markdown content.
// Clean, readable typography — not raw <pre> text.
// Mountable standalone; receives content as a prop.

import { FileText, Calendar, GitBranch } from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────

interface ArtifactDocViewProps {
  /** Human-readable artifact title, e.g. "Architecture Overview" */
  title: string;
  /** Raw markdown content to render */
  content: string | null;
  /** GitHub path or DB filename that was the source */
  source?: string | null;
  /** ISO timestamp of last update */
  updatedAt?: string | null;
  /** Called when user clicks the back button */
  onBack?: () => void;
}

// ─── Simple markdown-to-HTML renderer ────────────────────────────
// Intentionally minimal: headings, bold, italic, code, fenced code,
// links, and list items. Avoids a heavy dependency for this read-only view.

function renderMarkdown(md: string): string {
  return md
    // Fenced code blocks
    .replace(/```[\w]*\n([\s\S]*?)```/g, (_m, code: string) =>
      `<pre class="art-code-block"><code>${escHtml(code.trimEnd())}</code></pre>`)
    // Inline code
    .replace(/`([^`]+)`/g, (_m, code: string) => `<code class="art-code-inline">${escHtml(code)}</code>`)
    // H1-H4
    .replace(/^#### (.+)$/gm, (_m, t: string) => `<h4 class="art-h4">${t}</h4>`)
    .replace(/^### (.+)$/gm, (_m, t: string) => `<h3 class="art-h3">${t}</h3>`)
    .replace(/^## (.+)$/gm, (_m, t: string) => `<h2 class="art-h2">${t}</h2>`)
    .replace(/^# (.+)$/gm, (_m, t: string) => `<h1 class="art-h1">${t}</h1>`)
    // Bold + italic
    .replace(/\*\*\*(.+?)\*\*\*/g, (_m, t: string) => `<strong><em>${t}</em></strong>`)
    .replace(/\*\*(.+?)\*\*/g, (_m, t: string) => `<strong>${t}</strong>`)
    .replace(/\*(.+?)\*/g, (_m, t: string) => `<em>${t}</em>`)
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, href: string) =>
      `<a class="art-link" href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`)
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="art-hr" />')
    // List items
    .replace(/^[-*] (.+)$/gm, (_m, t: string) => `<li class="art-li">${t}</li>`)
    .replace(/^\d+\. (.+)$/gm, (_m, t: string) => `<li class="art-li-num">${t}</li>`)
    // Blank lines → paragraph breaks
    .replace(/\n\n/g, '</p><p class="art-p">')
    // Wrap in opening paragraph
    .replace(/^/, '<p class="art-p">')
    .replace(/$/, '</p>');
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

// ─── Component ───────────────────────────────────────────────────

export function ArtifactDocView({ title, content, source, updatedAt, onBack }: ArtifactDocViewProps) {
  const formattedDate = formatDate(updatedAt);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--orange)', fontSize: 12, padding: '0 0 10px 0',
            display: 'flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
          }}
        >
          ← Back
        </button>
      )}

      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <FileText size={16} style={{ color: 'var(--orange)', flexShrink: 0 }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {source && (
            <span style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 3, fontFamily: 'var(--font-mono)' }}>
              <GitBranch size={10} /> {source}
            </span>
          )}
          {formattedDate && (
            <span style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Calendar size={10} /> {formattedDate}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      {content == null ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-dim)', fontSize: 13, flexDirection: 'column', gap: 8,
        }}>
          <FileText size={28} style={{ opacity: 0.25 }} />
          <span>No content yet — run a sync to generate this document.</span>
        </div>
      ) : (
        <div
          className="art-doc-body"
          style={{
            flex: 1, overflowY: 'auto',
            fontSize: 13, lineHeight: 1.7, color: 'var(--text)',
          }}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
        />
      )}

      <style>{`
        .art-doc-body .art-p { margin: 0 0 0.75em; }
        .art-doc-body h1.art-h1 { font-size: 1.3em; font-weight: 700; margin: 1em 0 0.4em; color: var(--text); }
        .art-doc-body h2.art-h2 { font-size: 1.15em; font-weight: 600; margin: 0.9em 0 0.35em; color: var(--text); border-bottom: 1px solid var(--border); padding-bottom: 4px; }
        .art-doc-body h3.art-h3 { font-size: 1.05em; font-weight: 600; margin: 0.8em 0 0.3em; color: var(--text); }
        .art-doc-body h4.art-h4 { font-size: 0.95em; font-weight: 600; margin: 0.7em 0 0.25em; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.05em; }
        .art-doc-body .art-code-block { background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 10px 12px; overflow-x: auto; font-family: var(--font-mono); font-size: 11px; line-height: 1.5; margin: 0.5em 0; }
        .art-doc-body .art-code-inline { background: rgba(255,255,255,0.06); border-radius: 3px; padding: 1px 4px; font-family: var(--font-mono); font-size: 0.9em; }
        .art-doc-body .art-link { color: var(--orange); text-decoration: none; }
        .art-doc-body .art-link:hover { text-decoration: underline; }
        .art-doc-body .art-hr { border: none; border-top: 1px solid var(--border); margin: 1em 0; }
        .art-doc-body .art-li, .art-doc-body .art-li-num { margin: 0.2em 0 0.2em 1.2em; display: list-item; list-style-type: disc; }
        .art-doc-body .art-li-num { list-style-type: decimal; }
      `}</style>
    </div>
  );
}
